import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lock, FolderOpen, RefreshCw, Target, FileText } from "lucide-react";
import { Block, Bullets } from "@/components/course/LessonParts";
import { WorksheetBlocks } from "@/components/avenues/WorksheetBlocks";
import { StartCallButton } from "@/components/site/CallWindow";
import { Spinner } from "@/components/site/ProtectedRoute";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";

function PartNav({ data, base, n }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="avenue-part-nav">
      {data.parts.map((p) => (p.unlocked
        ? <Link key={p.n} to={`${base}/${p.n}`} className={`rounded-full px-4 py-2 text-sm font-semibold ${p.n === n ? "bg-[#0B2A4A] text-[#F5ECD7]" : "bg-white border border-[#0B2A4A]/15 text-[#0B2A4A]"}`} data-testid={`avenue-part-tab-${p.n}`}>Part {p.n} · {p.name}</Link>
        : <span key={p.n} className="rounded-full px-4 py-2 text-sm bg-[#0B2A4A]/5 text-slate-500 inline-flex items-center gap-1" data-testid={`avenue-part-tab-${p.n}`}><Lock className="h-3.5 w-3.5" /> Part {p.n}</span>))}
    </div>
  );
}

export default function AvenueLessonPage() {
  const { slug, part, userId } = useParams();
  const n = Number(part);
  const { user } = useAuth();
  const teaching = !!userId;
  const q = teaching ? `?student=${userId}` : "";
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [complete, setComplete] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState("");
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  const ver = useRef(0);
  useSeo({ title: "Avenue course", path: `/learn/${slug}/${n}`, noindex: true });

  const load = () => api.get(`/learn/${slug}${q}`).then(({ data: d }) => {
    setData(d);
    const p = d.progress[n] || {};
    setAnswers(p.answers || {});
    setComplete(!!p.complete);
    setDirty(false);
  }).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, [slug, n, userId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setInterval(() => { if (!dirtyRef.current && !document.hidden) load(); }, 5000);
    return () => clearInterval(t);
  }, [slug, n, userId]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = (c = complete, silent = true) => { const v = ver.current; return api.put(`/learn/${slug}/parts/${n}${q}`, { answers, complete: c }).then(() => { if (ver.current === v) setDirty(false); setComplete(c); if (!silent) toast.success("Saved to the profile"); }).catch((e) => toast.error(formatApiError(e))); };
  useEffect(() => {
    if (!dirty) return undefined;
    const t = setTimeout(() => save(), 1200);
    return () => clearTimeout(t);
  }, [answers, dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="avenue-lesson-locked"><Lock className="h-10 w-10 mx-auto text-[#F08A3C]" /><p className="mt-4 text-slate-700">{err}</p><Link to="/online-courses" className="mt-4 inline-block font-semibold text-[#F08A3C]">See online courses</Link></div>;
  if (!data) return <Spinner />;
  const p = data.parts[n - 1];
  const base = teaching ? `/host/teach/${userId}/learn/${slug}` : `/learn/${slug}`;
  const isHost = user?.role === "host";
  const setAnswer = (id, v) => { ver.current += 1; setAnswers((a) => ({ ...a, [id]: v })); setDirty(true); };
  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-8 py-10 flex flex-col gap-6" data-testid="avenue-lesson-page">
      <Link to={teaching && data.student ? `/host/people/${encodeURIComponent(data.student.email)}` : "/dashboard"} className="text-sm font-semibold text-[#147C88] inline-flex items-center gap-1" data-testid="avenue-back-link"><ArrowLeft className="h-4 w-4" /> Back</Link>
      {teaching && (
        <div className="rounded-2xl bg-[#0B2A4A] text-[#F5ECD7] p-4 flex flex-wrap items-center justify-between gap-3" data-testid="avenue-teaching-banner">
          <p className="text-sm"><span className="font-mono-ui text-[11px] tracking-widest text-[#F08A3C] mr-2">TEACHING MODE</span>{data.student?.name}</p>
          <div className="flex gap-2"><button onClick={load} className="inline-flex items-center gap-1 rounded-lg bg-white/10 border border-white/25 px-3 py-1.5 text-xs font-semibold" data-testid="avenue-refresh-btn"><RefreshCw className="h-3.5 w-3.5" /> Refresh answers</button><StartCallButton studentId={userId} /></div>
        </div>
      )}
      <div>
        <p className="font-mono-ui text-xs tracking-[0.3em] text-[#F08A3C]">AVENUE {String(data.number).padStart(2, "0")} · {data.title.toUpperCase()} · Esc DIGITAL NOMAD VILLAGE</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-[#0B2A4A]" data-testid="avenue-part-title">Part {n} · {p?.name}: {p?.title}</h1>
        <p className="mt-1 text-sm text-slate-600">{p?.week} · Target: {p?.goal}</p>
      </div>
      <PartNav data={data} base={base} n={n} />
      {!p?.unlocked ? (
        <div className="rounded-2xl bg-[#F5ECD7] p-6 text-slate-700" data-testid="avenue-part-locked"><Lock className="inline h-4 w-4 mr-1 text-[#F08A3C]" /> This part is still locked. Greg will unlock it on your account once it's confirmed.</div>
      ) : (
        <>
          {data.folder_url ? <a href={data.folder_url} target="_blank" rel="noopener noreferrer" className="self-start inline-flex items-center gap-2 rounded-xl bg-[#1A9AA8] text-white px-4 py-2.5 text-sm font-semibold" data-testid="avenue-folder-btn"><FolderOpen className="h-4 w-4" /> Open the Esc resource folder</a>
            : <span className="self-start inline-flex items-center gap-2 rounded-xl bg-[#0B2A4A]/5 px-4 py-2.5 text-sm text-slate-500" data-testid="avenue-folder-pending"><FolderOpen className="h-4 w-4" /> Resource folder coming soon</span>}
          {data.worksheets.some((w) => w.part === n) && (
            <Block label="Fill in or print" title="Worksheets and sheets for this part" testId="avenue-files">
              <div className="grid sm:grid-cols-2 gap-3">
                {data.worksheets.filter((w) => w.part === n).map((w) => (
                  <Link key={w.id} to={`${base}/worksheet/${w.id}`} className="flex items-center gap-3 rounded-xl border border-[#0B2A4A]/10 bg-[#FAF6EE] p-3 hover:border-[#1A9AA8]" data-testid={`avenue-file-${w.id}`}>
                    <FileText className="h-5 w-5 text-[#F08A3C] shrink-0" /><span className="text-sm"><span className="font-mono-ui text-[11px] text-slate-500">FILE {w.number}</span><br /><span className="font-semibold text-[#0B2A4A]">{w.title}</span></span>
                  </Link>
                ))}
              </div>
            </Block>
          )}
          {isHost && p.host_notes && <Block label="Host only" title="Teaching notes" testId="avenue-host-notes"><Bullets items={p.host_notes} /></Block>}
          {p.sections.map((s, i) => (
            <Block key={s.title} label={`Part ${n} · ${i + 1}`} title={s.title} testId={`avenue-section-${i}`}>
              {s.body && <p className="text-slate-700 leading-relaxed mb-3">{s.body}</p>}
              {s.bullets?.length > 0 && <Bullets items={s.bullets} />}
            </Block>
          ))}
          {p.friday_target?.length > 0 && <div className="rounded-2xl border-2 border-[#F08A3C] bg-[#F08A3C]/5 p-5" data-testid="avenue-friday-target"><p className="font-semibold text-[#0B2A4A] flex items-center gap-2"><Target className="h-4 w-4 text-[#F08A3C]" /> Friday target</p><Bullets items={p.friday_target} /></div>}
          <div className="flex flex-col gap-4" data-testid="avenue-worksheet">
            <div>
              <p className="font-mono-ui text-[11px] tracking-[0.3em] text-[#F08A3C]">YOUR WORKSHEET</p>
              <h2 className="mt-1 text-2xl font-bold text-[#0B2A4A]">Part {n} worksheet</h2>
              <p className="mt-1 text-sm text-slate-600">Work through the steps in order. The grey text in each box is an example. Type over it with your own answer. Everything saves automatically.</p>
            </div>
            <WorksheetBlocks blocks={p.worksheet} a={answers} set={setAnswer} />
          </div>
          <div className="sticky bottom-4 ml-16 sm:ml-0 rounded-2xl bg-white/95 backdrop-blur border border-[#0B2A4A]/10 p-4 flex flex-wrap items-center justify-between gap-3 shadow-lg">
            <span className="text-xs text-slate-600" data-testid="avenue-save-status">{dirty ? "Saving..." : "All changes saved"}{complete && " · Part complete"}</span>
            <button onClick={() => save(!complete, false)} className="rounded-xl bg-[#F08A3C] text-white px-5 py-2.5 text-sm font-semibold" data-testid="avenue-complete-btn">{complete ? "Mark not complete" : "Mark part complete"}</button>
          </div>
        </>
      )}
    </section>
  );
}
